import React, { memo, useMemo, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import {
  Grid,
  TextField,
  CustomIconButton,
  MenuItem,
  Typography,
  PrimaryButtonContained,
} from "../../../../../components";
import { mapKeys, omit, omitBy } from "lodash";
import { DeleteForever } from "../../../../../icons";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import { makeStyles } from "@material-ui/core/styles";
import VoiceFileInput from "../VoiceFileInput";
import { useDispatch } from "react-redux";
import {
  buildMasterTemplate,
  uploadWavFile,
} from "../../../../../actions/masterTemplate";
import {
  getDTMFKeyErrorKeyShouldMismatch,
  getDTMFKeyErrorPath,
  getStepErrorPath,
  invalidVoiceFileTypeErrorValue,
  keyShouldMismatchErrorValue,
  voiceFileUploadDefaultLabel,
  getDTMFKeyErrorPrefix,
} from "../../utils";

const ACTION_TRANSFER = "transfer";
const ACTION_PLAYBACK = "playback";
const ACTION_GO_TO = "goto";
const actionTypes = [ACTION_TRANSFER, ACTION_PLAYBACK, ACTION_GO_TO];
const keys = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "#", "*"];

const useStyles = makeStyles({
  sms: {
    height: 40,
  },
});

function VoiceDTMFInput({
  idx,
  readOnly,
  value = [],
  onChange,
  onDTMFFilePathChange,
  onSaveAsDraftInitial,
  gotoOptions,
  path,
  activeTab,
  templateType = "voiceTemplate",
}) {
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const masterTemplate = useSelector((state) => state.masterTemplate ?? {});
  const { errors = {} } = masterTemplate;
  const dtmfBaseFileErrors =
    errors[
      `${templateType}.freeswitch.dialPlan.${activeTab}.steps.${idx}.path`
    ];
  const [showUploadFileBtn, setShowUploadFileBtn] = useState(
    path || dtmfBaseFileErrors ? false : true
  );
  const classes = useStyles();

  const addAction = useCallback(() => {
    onChange(idx, [...value, {}]);
  }, [idx, onChange, value]);

  const onSelectAction = useCallback(
    (ev, stepIdx) => {
      const { key } = value[stepIdx];
      value[stepIdx] = { key }; // reset
      value[stepIdx]["action"] = ev.target.value;
      onChange(idx, value);
    },
    [idx, onChange, value]
  );

  const onSelectKey = useCallback(
    (ev, stepIdx) => {
      const selectedKey = ev.target.value;

      const previousKeySelected = value[stepIdx].key;
      // Update the key value
      value[stepIdx]["key"] = selectedKey;
      onChange(idx, value);

      // Cleanup/update opt out key error
      const optOutKey = masterTemplate?.[templateType]?.optOutKey;
      const existingKeyShouldMismatchError = getDTMFKeyErrorKeyShouldMismatch(
        idx,
        previousKeySelected,
        activeTab,
        templateType
      );
      let newErrors = omit(errors, existingKeyShouldMismatchError);

      if (selectedKey === optOutKey) {
        const newKeyShouldMismatchError = getDTMFKeyErrorKeyShouldMismatch(
          idx,
          selectedKey,
          activeTab,
          templateType
        );
        newErrors = {
          ...errors,
          [newKeyShouldMismatchError]: keyShouldMismatchErrorValue,
        };
      }
      dispatch(
        buildMasterTemplate({
          errors: newErrors,
        })
      );
    },
    [
      dispatch,
      errors,
      onChange,
      idx,
      value,
      templateType,
      masterTemplate,
      activeTab,
    ]
  );

  const onSelectGoto = useCallback(
    (ev, stepIdx) => {
      value[stepIdx]["step"] = ev.target.value;
      onChange(idx, value);
    },
    [idx, onChange, value]
  );

  const onChangeTransfer = useCallback(
    (sms, stepIdx) => {
      value[stepIdx]["transferNumber"] = sms;
      onChange(idx, value);
    },
    [onChange, idx, value]
  );

  const onDelete = useCallback(() => {
    setShowUploadFileBtn(true);
    onDTMFFilePathChange(idx, "");
  }, [onDTMFFilePathChange, idx]);

  const onRejectVoiceFile = useCallback(
    (key) => {
      // handle DTMF voice file upload errors for keys
      if (key || key === 0) {
        return dispatch(
          buildMasterTemplate({
            errors: {
              ...errors,
              [getDTMFKeyErrorPath(idx, key, activeTab, templateType)]: t(
                invalidVoiceFileTypeErrorValue
              ),
            },
          })
        );
      }
      // handle DTMF base voice file uploads
      return dispatch(
        buildMasterTemplate({
          errors: {
            ...errors,
            [getStepErrorPath(idx, activeTab, templateType)]: t(
              invalidVoiceFileTypeErrorValue
            ),
          },
        })
      );
    },
    [dispatch, activeTab, errors, t, idx, templateType]
  );

  const onUploadVoiceFileDTMF = useCallback(
    async ([file], stepIdx, key) => {
      try {
        const path = await dispatch(uploadWavFile(file));
        value[stepIdx]["path"] = path;
        onChange(idx, value);
      } catch (err) {
        onRejectVoiceFile(key);
      }
    },
    [dispatch, value, onChange, idx]
  );

  const onUploadBaseVoiceFileDTMF = useCallback(
    async ([file]) => {
      try {
        await onSaveAsDraftInitial(true, true); // Pass true for initial and true for isDTMFUpload
        const path = await dispatch(uploadWavFile(file));
        onDTMFFilePathChange(idx, path);
      } catch (err) {
        onRejectVoiceFile();
      }
    },
    [dispatch, onDTMFFilePathChange, onSaveAsDraftInitial, idx]
  );

  const cleanupDTMFStepErrors = useCallback(
    (deletedIndex, key) => {
      const stepPrefix = getDTMFKeyErrorPrefix(
        idx,
        key,
        activeTab,
        templateType
      );
      let updatedErrors = { ...errors };

      // Remove errors for the deleted step
      const cleanedErrors = omitBy(updatedErrors, (_, key) =>
        key.startsWith(stepPrefix)
      );

      // Shift down the remaining step errors including opt out key errors
      const shiftedErrors = mapKeys(cleanedErrors, (value, key) => {
        // Match step key errors
        const stepMatch = key.match(
          new RegExp(
            `^(voiceTemplate\\.freeswitch\\.dialPlan\\.${activeTab}\\.steps\\.(\\d+)\\.keys\\.${key}.*)$`
          )
        );

        if (stepMatch) {
          const index = parseInt(stepMatch[2], 10);

          if (index > deletedIndex) {
            const newIndex = index - 1;
            return key.replace(`steps.${index}.`, `steps.${newIndex}.`);
          }
          return key;
        }

        return key; // leave other keys unchanged
      });
      return shiftedErrors;
    },
    [activeTab, idx, errors, masterTemplate, templateType]
  );

  const deleteStep = useCallback(
    (stepIdx, key) => {
      onChange(
        idx,
        value.filter((_, i) => i !== stepIdx),
        cleanupDTMFStepErrors(stepIdx, key)
      );
    },
    [onChange, idx, value]
  );

  const takenKeys = value
    .map((step) => step.key)
    .filter((key) => key !== undefined);
  const availKeys = keys.filter((key) => takenKeys.indexOf(key) < 0);

  const readOnlyProps = useMemo(() => ({ readOnly }), [readOnly]);
  const getDTMFKeyErrorKeyShouldMismatchValue = (
    stepIdx,
    selectedKey,
    activeTab,
    templateType
  ) =>
    errors[
      getDTMFKeyErrorKeyShouldMismatch(
        stepIdx,
        selectedKey,
        activeTab,
        templateType
      )
    ];
  const getDTMFKeyErrorPathValue = (
    stepIdx,
    selectedKey,
    activeTab,
    templateType
  ) =>
    errors[getDTMFKeyErrorPath(stepIdx, selectedKey, activeTab, templateType)];
  const getDTMFBaseErrorPathValue = (stepIdx, activeTab, templateType) =>
    errors[getStepErrorPath(stepIdx, activeTab, templateType)];
  return (
    <Grid
      container
      spacing={2}
      style={{
        border: "1px solid #d3d4d5",
        margin: "0.2em",
        borderRadius: "5px",
      }}
    >
      <Grid item xs={12} sm={12} md={12}>
        <Typography>{t("DTMF")}</Typography>
      </Grid>

      <Grid item xs={12} sm={12} md={12}>
        <Grid container direction="column" spacing={2}>
          {value.map((step, stepIdx) => (
            <Grid item key={stepIdx}>
              <Grid container spacing={1} wrap="nowrap" alignItems="flex-start">
                <Grid item xs>
                  <TextField
                    disabled={readOnly}
                    data-test-id={`master-template-voice-dtmf-key-${idx}-${stepIdx}`}
                    select
                    label={t("Key")}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={step.key ? step.key : " "}
                    onChange={(ev) => onSelectKey(ev, stepIdx)}
                    InputProps={readOnlyProps}
                    error={
                      Boolean(
                        getDTMFKeyErrorKeyShouldMismatchValue(
                          idx,
                          step.key,
                          activeTab,
                          templateType
                        )
                      ) &&
                      step.key?.toString() ===
                        masterTemplate?.[templateType]?.optOutKey?.toString()
                    }
                    helperText={
                      step.key?.toString() ===
                      masterTemplate?.[templateType]?.optOutKey?.toString()
                        ? getDTMFKeyErrorKeyShouldMismatchValue(
                            idx,
                            step.key,
                            activeTab,
                            templateType
                          )
                        : null
                    }
                  >
                    {[step.key ? step.key : "-", ...availKeys].map((header) => (
                      <MenuItem key={header} value={header}>
                        {header}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs>
                  <TextField
                    disabled={readOnly}
                    data-test-id={`master-template-voice-dtmf-data-field-${idx}-${stepIdx}`}
                    select
                    label={t("Action")}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={step.action ? step.action : ""}
                    onChange={(ev) => {
                      onSelectAction(ev, stepIdx);
                    }}
                    InputProps={readOnlyProps}
                    error={null}
                    helperText={null}
                  >
                    {actionTypes.map((header) => (
                      <MenuItem key={header} value={header}>
                        {header}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item>
                  {(() => {
                    const { action } = step;
                    const key = step?.key || null;
                    switch (action) {
                      case ACTION_TRANSFER:
                        return (
                          <PhoneInput
                            disabled={readOnly}
                            data-test-id={`master-template-action-transfer-${idx}-${stepIdx}`}
                            inputClass={classes.sms}
                            label={t("Transfer Number")}
                            country={"us"}
                            value={
                              step.transferNumber
                                ? `${step.transferNumber}`
                                : ""
                            }
                            onChange={(sms) => onChangeTransfer(sms, stepIdx)}
                            variant="outlined"
                            size="small"
                            fullWidth
                          />
                        );
                      case ACTION_PLAYBACK:
                        return (
                          <VoiceFileInput
                            idx={stepIdx}
                            readOnly={readOnly || !step.key}
                            error={Boolean(
                              getDTMFKeyErrorPathValue(
                                idx,
                                key,
                                activeTab,
                                templateType
                              )
                            )}
                            onUpload={(file, idx) =>
                              onUploadVoiceFileDTMF(file, idx, key)
                            }
                            onReject={() => onRejectVoiceFile(key)}
                            label={
                              getDTMFKeyErrorPathValue(
                                idx,
                                key,
                                activeTab,
                                templateType
                              )
                                ? t(invalidVoiceFileTypeErrorValue)
                                : truncateLongString(
                                    parseFileName(step.path)
                                  ) || t(voiceFileUploadDefaultLabel)
                            }
                            filePath={step.path}
                          />
                        );
                      case ACTION_GO_TO:
                        return (
                          <TextField
                            disabled={readOnly}
                            data-test-id={`master-template-voice-dtmf-goto-${idx}-${stepIdx}`}
                            select
                            label={t("Go to Step")}
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={step.step ? step.step : " "}
                            onChange={(ev) => onSelectGoto(ev, stepIdx)}
                            InputProps={readOnlyProps}
                            error={null}
                            helperText={null}
                          >
                            {gotoOptions.map((header) => (
                              <MenuItem key={header} value={header}>
                                {header}
                              </MenuItem>
                            ))}
                          </TextField>
                        );
                      default:
                        return "";
                    }
                  })()}
                </Grid>
                <Grid item>
                  <CustomIconButton
                    disabled={readOnly}
                    data-test-id={`master-template-delete-voice-dtmf-${stepIdx}`}
                    size="small"
                    color="error"
                    onClick={() => deleteStep(stepIdx, step.key)}
                  >
                    <DeleteForever />
                  </CustomIconButton>
                </Grid>
              </Grid>
            </Grid>
          ))}
          <Grid item>
            {showUploadFileBtn ? (
              <Grid container spacing={2} justify="flex-end">
                <Grid item>
                  <PrimaryButtonContained
                    disabled={readOnly}
                    aria-controls="customized-menu"
                    aria-haspopup="true"
                    onClick={() => setShowUploadFileBtn(false)}
                    error={false}
                  >
                    {t("Add Voice File")}
                  </PrimaryButtonContained>
                </Grid>
              </Grid>
            ) : (
              <Grid container alignItems="flex-start">
                <Grid item xs={11} md={11} sm={11}>
                  <VoiceFileInput
                    readOnly={readOnly}
                    error={getDTMFBaseErrorPathValue(
                      idx,
                      activeTab,
                      templateType
                    )}
                    onUpload={onUploadBaseVoiceFileDTMF}
                    onReject={onRejectVoiceFile}
                    label={
                      getDTMFBaseErrorPathValue(idx, activeTab, templateType)
                        ? t(invalidVoiceFileTypeErrorValue)
                        : truncateLongString(parseFileName(path)) ||
                          t(voiceFileUploadDefaultLabel)
                    }
                    filePath={path}
                  />
                </Grid>
                <Grid item>
                  <CustomIconButton
                    disabled={readOnly}
                    data-test-id={`master-template-delete-voice-item-button-${idx}`}
                    size="small"
                    color="error"
                    onClick={() => onDelete()}
                  >
                    <DeleteForever />
                  </CustomIconButton>
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid item>
            <Grid container spacing={2} justify="flex-end">
              <Grid item>
                <PrimaryButtonContained
                  disabled={readOnly}
                  aria-controls="customized-menu"
                  aria-haspopup="true"
                  onClick={addAction}
                  error={false}
                >
                  {t("Add DTMF Step")}
                </PrimaryButtonContained>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

function truncateLongString(str) {
  if (!str) {
    return false;
  }
  const maxLength = 50;
  if (str.length > maxLength) {
    return str.slice(0, maxLength) + "...";
  }
  return str;
}

function parseFileName(path) {
  if (!path) return false;

  const arr = path.split("/");
  return arr[arr.length - 1];
}

export default memo(VoiceDTMFInput);
